import React from "react";
import {
    DataGrid,
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Typography,
    Chip,
    Modal,
    Stack,
    Divider,
    Checkbox,
    FormControlLabel,
    Tooltip,
} from "@mui/material";
import {
    AlertCircle,
    Search as SearchIcon,
} from 'react-feather';
import NavItem from '../../layout/NavBar/NavItem';
import { useDispatch, useSelector } from "react-redux";
import ReviewPage from "../billing/Validation";
import EstimateView from "../billing/EstimateView";
import { Form, Label, Input } from 'reactstrap';
import AgreementModal from '../../components/AgreementModal';
import './plans.css';
import SplitToken from '../../mixins/SplitToken';
import Page from '../../components/Page';
import { canAccess } from '../../permissions/canAccess';
import { AppViewIds } from '../../permissions/appViewIds';
import StaticValidationView from './StaticValidationView';
import { StorageLimitIndicator } from '../../components/StorageLimitIndicator';
import {
    useCustomerFiles,
    useDeleteFiles,
    useFileCount,
    useUpdateAllFilesAllowDownload,
    useUpdateFileDownload,
    useFetchValidationFile,
    useDownloadPaid,
    useFetchPolicyPdf,
} from '../../hooks/useFiles';
import { useUpdateInvoiceStatus } from '../../hooks/useInvoices';
import { useAdminCompany } from '../../context/AdminCompanyContext';
import { useSAConfig } from '../../hooks/useSAConfig';
import { useUpdateAgreement } from '../../hooks/useAuth';
import { usePermissionState } from '../../stores/permissionStore';

const PlanInboxTable = ({managePlans}) => {
    /// displays all plans that have been sent for review from a company to the customer
    const dispatch = useDispatch();
    const [review, setReview] = React.useState(false);
    const [validationView, setValidationView] = React.useState(false);
    const [invoiceModal, setInvoiceModal] = React.useState(false);
    const deleteFiles = useDeleteFiles();
    const pauseOverlays = review || validationView || invoiceModal || deleteFiles.isLoading;

    function handleOpenValidationView (){
        setValidationView(true);
    };
    function handleCloseValidationView (){
        setValidationView(false);
        dispatch({ type: "VALIDATION_RESET" });
    };

    const { data: saConfigData = [] } = useSAConfig({ pauseRefetch: pauseOverlays });
    const [internalAnn, setInternalAnn] = React.useState();
    function initValues(){
        for (let i = 0; i < saConfigData.length; i++){
            if (saConfigData[i].configName == 'Internal Announcements'){
                setInternalAnn(saConfigData[i].configValue)
            }
        }
    }

    const updateInvoiceStatus = useUpdateInvoiceStatus();
    const updateFileDownload = useUpdateFileDownload();
    const updateAllFilesAllowDownload = useUpdateAllFilesAllowDownload();
    const fetchValidationFile = useFetchValidationFile();
    const downloadPaid = useDownloadPaid();
    const fetchPolicyPdf = useFetchPolicyPdf();
    const updateAgreement = useUpdateAgreement();
    const user = SplitToken();
    const [edit, setEdit] = React.useState(false);
    const [invoiceModelObj, setInvoiceModelObj] = React.useState({});
    const company = useSelector((state) => state.CompanyReducer.userCompanies[0]);

    const [paginationModel, setPaginationModel] = React.useState({pageSize: 25, page: 0});
    const customerSelect = useSelector((state) => state.UserReducer.customerSelection);

    const permissionState = usePermissionState();
    const canManagePlanInbox = canAccess(AppViewIds.PlanInboxAdmin, permissionState);
    const { companyData } = useAdminCompany();

    let selectedCompanyValues = {};
    if(managePlans == true){
        selectedCompanyValues = companyData;
    } else{
        selectedCompanyValues = company;
    };

    const { data: fileStorageCount = 0 } = useFileCount({
        companyId: selectedCompanyValues?.id,
        enabled: Boolean(selectedCompanyValues?.id),
        notify: !pauseOverlays,
    });

    const customerFilesCustomerId = managePlans === true ? customerSelect.userCompanyId : user.id;

    const { data: customerFilesData, isFetching: isDeleteGridLoading } = useCustomerFiles({
        companyId: selectedCompanyValues?.id,
        customerId: customerFilesCustomerId,
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        enabled: Boolean(selectedCompanyValues?.id && customerFilesCustomerId),
        pauseRefetch: pauseOverlays,
    });

    const customerFiles = customerFilesData?.filesDTO ?? [];
    const filesCount = customerFilesData?.totalFileCount ?? 0;
    const inboxTotal = filesCount ?? 0;
    const allowDlCount = customerFilesData?.inboxAllowDownloadAllowed ?? 0;

    React.useEffect(() => {
        initValues();
    },[paginationModel]);

    React.useEffect(() => {
        initValues();
    },[saConfigData]);

    const rowCountRef = React.useRef(filesCount || 0);

    const rowCount = React.useMemo(() => {
      if (filesCount !== undefined) {
        rowCountRef.current = filesCount;
      }
      return rowCountRef.current;
    }, [filesCount]);

    /// for checking user data to allow user to download if policy is accepted or not
    const userdata = useSelector((state)=> state.UserReducer.user);
    const pdf = useSelector((state)=> state.FileReducer.PDFfile.file);
    const [pdfUrl, setPdfUrl] = React.useState(`${pdf}`);
    const [pdfStatus, setPdfStatus] = React.useState();
    let userD = {
        Id: userdata.id,
        hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
        hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy,
        hasConfirmedEmail: userdata.hasConfirmedEmail,
        hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
    };

    function showPDF(cellValues){ // hadles the double click to bring up modal to edit crop amount
        handleOpen();
    };

    function showInvoice(x){
        setInvoiceModelObj({
            id: x?.id,
            name: x?.name,
            companyId: x?.companyId,
            isProcessed: x?.isProcessed,
            url: x?.url,
            version: x?.version,
            reportUrl: x?.reportUrl,
            created: x?.created,
            updated: x?.updated,
            deleted: x?.deleted,
            summaryUrl: x?.summaryUrl,
            paidFileUrl: x?.paidFileUrl,
            isAccepted: x?.isAccepted,
            isPaidFor: x?.isPaidFor,
            sequencingErrorCode: x?.sequencingErrorCode,
            paidTextUrl: x?.paidTextUrl,
            pointCount: x?.pointCount,
            estimateId: x?.estimateId,
            dwgUrl: x?.dwgUrl,
            pointList: x?.pointList,

            customerStatus: x?.customerStatus,
            companyContact: x?.companyContact,
            companyStreet: x?.companyStreet,
            companyCity: x?.companyCity,
            companyState: x?.companyState,
            companyZip: x?.companyZip,
            companyCountry: x?.companyCountry
        })
        handleInvoiceOpen();
        fetchValidationFile.mutate(x.id);
    };

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleInvoiceOpen = () => setInvoiceModal(true);
    const handleInvoiceClose = () => setInvoiceModal(false);

    let userToUpdate = {
        Id: userdata.id,
        hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
        hasAcceptedDownloadPolicy: userdata.hasAcceptedDownloadPolicy,
        hasConfirmedEmail: userdata.hasConfirmedEmail,
        hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
    };

    function changePDFStatus(event){
        setPdfStatus(!pdfStatus);
        if (event === true) {
            userToUpdate = {
                Id: userdata.id,
                hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
                hasAcceptedDownloadPolicy: 1,
                hasConfirmedEmail: userdata.hasConfirmedEmail,
                hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
            }
            updateAgreement.mutate(userToUpdate);
            handleClose();
        }
        else if (event === false) {
            userToUpdate = {
                Id: userdata.id,
                hasAcceptedUploadPolicy: userdata.hasAcceptedUploadPolicy,
                hasAcceptedDownloadPolicy: 0,
                hasConfirmedEmail: userdata.hasConfirmedEmail,
                hasReadPrivacyPolicy: userdata.hasReadPrivacyPolicy,
            }
            updateAgreement.mutate(userToUpdate);
        }
    };

    const isMountedRef = React.useRef(true);
    React.useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    const setPdfUrlIfMounted = React.useCallback((url) => {
        if (isMountedRef.current) {
            setPdfUrl(url);
        }
    }, []);

    React.useEffect(() => {
        if (!selectedCompanyValues?.id || !user?.id) return;

        if (userdata.hasAcceptedDownloadPolicy === 0) {
            if (isMountedRef.current) setPdfStatus(false);
        } else if (userdata.hasAcceptedDownloadPolicy === 1) {
            if (isMountedRef.current) setPdfStatus(true);
        }

        if (pauseOverlays) {
            return;
        }

        const controller = new AbortController();
        fetchPolicyPdf.mutate({
            pdfType: 2,
            setPdfUrl: setPdfUrlIfMounted,
            signal: controller.signal,
        });

        return () => {
            controller.abort();
        };
    }, [selectedCompanyValues?.id, user?.id, pauseOverlays, setPdfUrlIfMounted, userdata.hasAcceptedDownloadPolicy]);

    const handleDownload = (file) => {
        downloadPaid.mutate(file);
    };

    function handleAcceptAndDownload(item){
        let acceptInvoice = {
            id: item.botmarkEstimateId,
            isInvoiceAccepted: item.botmarkIsInvoiceAccepted,
            OpenInvoice: true,
            customerInvoiceStatus: 3
        };
        updateInvoiceStatus.mutate({
            invoice: acceptInvoice,
            currentStatus: item.botmarkIsInvoiceAccepted,
            item,
            onPaidDownload: (paidItem) => downloadPaid.mutate(paidItem),
        });
    };

    function updateDownload(params){
        let downloadA = !params.allowDownload;
        updateFileDownload.mutate(
            { id: params.id, allowDownload: downloadA },
            {
                onSuccess: (id) => {
                    fetchValidationFile.mutate(id);
                },
            },
        );
    };

    const [rowSelectionModel, setRowSelectionModel] = React.useState([]);

    function multiDelete(items){
        swal({
            title: "DELETE FILE(S)",
            text: `Delete ${items.length} file(s) forever? This cannot be undone.`,
            icon: "warning",
            buttons: true,
            dangerMode: false,
        })
        .then((willUpdate) => {
            if (willUpdate) {
                deleteFiles.mutate(rowSelectionModel, {
                    onSuccess: () => {},
                });
            } else {
                swal({
                    title: `File(s) not Deleted.`
                });
        }})
    };

    const allFilesAllowDownload =
        inboxTotal > 0 && allowDlCount === inboxTotal;
    const mixedAllowDownload =
        allowDlCount > 0 && allowDlCount < inboxTotal;

    function bulkAllowDownloadAllFiles(nextAllowDownload) {
        if (!canManagePlanInbox || !selectedCompanyValues?.id) {
            return;
        }
        updateAllFilesAllowDownload.mutate({
            fileCustomerId: selectedCompanyValues.id,
            allowDownload: nextAllowDownload,
            refresh: true,
        });
    }

    const columns = [
        { field: 'name', headerName: 'Project Name', width: 250, headerClassName: 'header', editable: false},
        { field: 'buttonStatus', headerName: 'File', width: 300, headerClassName: 'header', sortable: false, renderCell: (params) => (
            File(params.row)
        )},
        { field: 'botmarkInvoiceUpdated', headerName: 'Updated', width: 175, headerClassName: 'header', renderCell: (params) => {
            let date = new Date(params.row.botmarkInvoiceUpdated);
            return date.toLocaleString()
        }},
        { field: 'processorName', headerName: 'Processor', width: 150, headerClassName: 'header', editable: false},
        { field: 'validationStatus', headerName: 'Validation & Summary', width: 240, headerClassName: 'header', sortable: false,
            renderCell: (cellValues) => {
                if(cellValues.row.isAccepted === 1 &&
                    (cellValues.row.isProcessed === 1 ||
                    cellValues.row.isProcessed === 3 ||
                    cellValues.row.isProcessed === 4) && cellValues.row.sequencingErrorCode <= 1){
                    cellValues.row.validationStatus = "Validation";
                    return(
                        <Button variant="contained" sx={{ bgcolor: `info.light`}}
                            onClick={() => {
                                fetchValidationFile.mutate(cellValues.row.id)
                                handleOpenValidationView();
                        }}>
                            View V & S
                        </Button>
                    )
                } else if (cellValues.row.isProcessed == 2){
                    cellValues.row.validationStatus = "Skipped Processing"
                    return(
                        <Chip icon={<AlertCircle/>} variant="outlined" color="warning" label="SKIPPED PROCESSING"/>
                    )
                } else if (cellValues.row.sequencingErrorCode == 88){
                    cellValues.row.validationStatus = "Processing Failed"
                    return(
                        <Button disabled variant="contained" sx={{ bgcolor: `error.light`}}>
                            Processing Failed
                        </Button>
                    )
                } else if (cellValues.row.sequencingErrorCode > 1 && cellValues.row.sequencingErrorCode != 20){
                    cellValues.row.validationStatus = "Critical Error"
                    return(
                        <Button variant="contained" sx={{ bgcolor: `error.light`}}
                            onClick={() => {
                                fetchValidationFile.mutate(cellValues.row.id)
                                handleOpenValidationView();
                        }}>
                            View V & S (Critical)
                        </Button>
                    )
                } else if (cellValues.row.sequencingErrorCode == 20){
                    cellValues.row.validationStatus = "Non Critical Error";
                    return(
                        <Button variant="contained" color="warning"
                            onClick={() => {
                                fetchValidationFile.mutate(cellValues.row.id)
                                handleOpenValidationView();
                        }}>
                            View V & S (Non Critical)
                        </Button>
                    )
                }
            }
        },
        { field: 'allowDownload', headerName: 'Allow Download', width: 125, headerClassName: 'header', renderCell: (params) => {
            if (canManagePlanInbox){
                return(
                    <Label check>
                        <Input type="switch" role="switch" checked={params.row.allowDownload} value={params.row.allowDownload} onChange={() => updateDownload(params.row)}/>
                    </Label>
                )
            } else {
                return(
                    <span>N/A</span>
                )
            }
        }},
        { field: 'deleteReject', headerName: '', width: 125, headerClassName: 'header', sortable: false,
            renderCell: (cellValues) => {
                if (canManagePlanInbox){
                    cellValues.row.deleteReject = "Delete";
                    if (rowSelectionModel.length == 0 ){
                        return(
                            <Button className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}
                                onClick={() => {
                                    swal({
                                        title: "DELETE FILE",
                                        text: `Delete this file forever? This cannot be undone.`,
                                        icon: "warning",
                                        buttons: true,
                                        dangerMode: false,
                                    })
                                    .then((willUpdate) => {
                                        if (willUpdate) {
                                            deleteFiles.mutate([cellValues.row.id], {
                                                onSuccess: () => {},
                                            });
                                        } else {
                                            swal({
                                                title: `File not Deleted.`
                                            });
                                    }})
                            }}>
                                Delete
                            </Button>
                        )
                    } else {
                        return(
                            <Button disabled className="delBtn" variant="contained" sx={{ bgcolor: `error.light`}}>Delete</Button>
                        )
                    }
                } else if(cellValues.row.isInvoiceAccepted == 2){
                    cellValues.row.deleteReject = "Reject";
                    return(
                        <Button className="delBtn" variant="contained" color="warning"
                            onClick={()=> updateInvoiceStatus.mutate({ invoice: {id: cellValues.row.estimateId, isInvoiceAccepted: 3, OpenInvoice: true}, handleInvoiceClose, currentStatus: cellValues.row.isInvoiceAccepted, recall: false })}
                        >Reject</Button>
                    )
                }
            }
        },
    ];

    function File(x) {
        if (canManagePlanInbox){

            if ( x.isPaidFor === 0 && x.botmarkOpenInvoice === true && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 1 ){
                x.buttonStatus = "Review Estimate";
                return (
                    // brings user to estimate page
                    <Button variant="contained" color="primary" onClick={() => showInvoice(x)}>
                            Review Estimate
                        </Button>
                    )
                }
                else if ( x.isPaidFor === 0 && x.openInvoice === true && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 3 ){
                    x.buttonStatus = "Response Pending";
                    return (
                        /// shows rejected invoice
                        <Button variant="contained" color="secondary" disabled>
                            Response Pending
                        </Button>
                    )
                }
                else if( x.customerStatus === 2){
                    x.buttonStatus = "Locked";
                    return (
                        /// shows that customer is locked based on status with company
                        <Button variant="contained" color="secondary" disabled>
                            <div>Locked Customer: Contact <b>{x.companyContact}</b></div>
                        </Button>
                    )
                }
                else if( x.customerStatus === 0){
                    x.buttonStatus = "Pending";
                    return (
                        /// shows that customer status hasnt been approved
                        <Button variant="contained" color="secondary" disabled>
                            <div>Pending Customer: Contact <b>{x.companyContact}</b></div>
                        </Button>
                    )
                }
                else if( x.customerStatus === 3){
                    x.buttonStatus = "Locked";
                    return (
                        /// shows that customer is locked based on status with company
                        <Button variant="contained" color="secondary" disabled>
                            <div>Locked Customer: Contact <b>{x.companyContact}</b></div>
                        </Button>
                    )
                }
                else if( x.isPaidFor === 0 && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 4 && x.botmarkCustomerInvoiceStatus === (null||0||1||2) && x.customerStatus === 1){
                    x.buttonStatus = "Accept & Download";
                    if(fileStorageCount < 100){
                        return (
                            /// download paid file/ accept download policy
                            <div>
                                { userdata.hasAcceptedDownloadPolicy === 0 ?
                                    <Button variant="contained" color="primary" onClick={() => showPDF(x)}>
                                        Download Policy
                                    </Button>
                                    :
                                    <Button variant="contained" color="success" onClick={() => handleAcceptAndDownload(x)}>
                                        Accept & Download
                                    </Button>
                                }
                            </div>
                        )
                    }else {
                        return (
                            <Box>
                                <Typography fontWeight="bold">
                                    File Storage Limit Reached
                                </Typography>
                            </Box>
                        )
                    }
                }
                else if( x.isPaidFor === 0 && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 4 && x.botmarkCustomerInvoiceStatus === 3 && x.customerStatus === 1){
                    x.buttonStatus = "Download";
                    return (
                        /// download paid file/ accept download policy
                        <div>
                            { userdata.hasAcceptedDownloadPolicy === 0 ?
                                <Button variant="contained" color="primary" onClick={() => showPDF(x)}>
                                    Download Policy
                                </Button>
                                :
                                <Button variant="contained" color="success" onClick={() => handleDownload(x)}>
                                    Download
                                </Button>
                            }
                        </div>
                    )
                }
            } else {
                if ( x.isPaidFor === 0 && x.botmarkOpenInvoice === true && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 1 ){
                    x.buttonStatus = "Awaiting Review";
                    return (
                        // brings user to estimate page
                        <Button variant="contained" color="primary" disabled>
                            Awaiting Review
                        </Button>
                    )
                }
                else if ( x.isPaidFor === 0 && x.botmarkOpenInvoice === true && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 3 ){
                    x.buttonStatus = "Response Pending";
                    return (
                        /// shows rejected invoice
                        <Button variant="contained" color="secondary" disabled>
                            Response Pending
                        </Button>
                    )
                }
                else if( x.customerStatus === 2){
                    x.buttonStatus = "Locked";
                    return (
                        /// shows that customer is locked based on status with company
                        <Button variant="contained" color="secondary" disabled>
                            <div>Locked Customer: Contact <b>{x.companyContact}</b></div>
                        </Button>
                    )
                }
                else if( x.customerStatus === 0){
                    x.buttonStatus = "Pending";
                    return (
                        /// shows that customer status hasnt been approved
                        <Button variant="contained" color="secondary" disabled>
                            <div>Pending Customer: Contact <b>{x.companyContact}</b></div>
                        </Button>
                    )
                }
                else if( x.customerStatus === 3){
                    x.buttonStatus = "Locked";
                    return (
                        /// shows that customer is locked based on status with company
                        <Button variant="contained" color="secondary" disabled>
                            <div>Locked Customer: Contact <b>{x.companyContact}</b></div>
                        </Button>
                    )
                }
                else if( x.isPaidFor === 0 && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 4 && x.botmarkCustomerInvoiceStatus === (null||0||1||2) && x.customerStatus === 1){
                    x.buttonStatus = "Accept & Download";
                    if(fileStorageCount < 100){
                        return (
                            /// download paid file/ accept download policy
                            <div>
                                { userdata.hasAcceptedDownloadPolicy === 0 ?
                                    <Button variant="contained" color="primary" onClick={() => showPDF(x)}>
                                        Download Policy
                                    </Button>
                                    :
                                    <Button variant="contained" color="success" onClick={() => handleAcceptAndDownload(x)}>
                                        Accept & Download
                                    </Button>
                                }
                            </div>
                        )
                    } else {
                        return (
                            <Box>
                                <Typography fontWeight="bold">
                                    File Storage Limit Reached
                                </Typography>
                            </Box>
                        )
                    }
                }
                else if( x.isPaidFor === 0 && x.botmarkInvoiceIsSent === true && x.botmarkIsInvoiceAccepted === 4 && x.botmarkCustomerInvoiceStatus === 3 && x.customerStatus === 1){
                    x.buttonStatus = "Download";
                    return (
                        /// download paid file/ accept download policy
                        <div>
                            { userdata.hasAcceptedDownloadPolicy === 0 ?
                                <Button variant="contained" color="primary" onClick={() => showPDF(x)}>
                                    Download Policy
                                </Button>
                                :
                                <Button variant="contained" color="success" onClick={() => handleDownload(x)}>
                                    Download
                                </Button>
                            }
                        </div>
                    )
                }
        }
    };
    const rows = customerFiles;

    return (
        <Page title="Plan Inbox">
        <Box style={{paddingRight:'30px', paddingLeft:'30px', height: '65vh'}}>
            <p style={{textAlign:'left', backgroundColor:'lightblue', padding:5}}><b>Announcements:</b> {internalAnn}</p>

            <Box
                sx={{
                    margin: "5px",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span style={{ textAlign: "left" }}>
                        <b>Current File Count: </b>
                        <StorageLimitIndicator count={fileStorageCount} />
                    </span>
                    {canManagePlanInbox && selectedCompanyValues?.id ? (
                        <>
                            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />
                            <Tooltip
                                title="Turn on Allow download for every plan in this list so your plan processor can download those plans. You can still change individual rows. When a new plan appears here, it may allow processor download automatically if every plan already does."
                                arrow
                                placement="top"
                                enterDelay={400}
                            >
                                <FormControlLabel
                                    sx={{ mr: 0, ml: 0 }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            color="success"
                                            checked={allFilesAllowDownload}
                                            indeterminate={mixedAllowDownload}
                                            disabled={inboxTotal === 0}
                                            onChange={(e) => bulkAllowDownloadAllFiles(e.target.checked)}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" color="text.secondary" component="span">
                                            Allow all downloads
                                        </Typography>
                                    }
                                />
                            </Tooltip>
                        </>
                    ) : null}
                </Stack>
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                        borderLeft: { xs: "none", sm: "1px solid" },
                        borderColor: "divider",
                        pl: { xs: 0, sm: 2 },
                        ml: { xs: 0, sm: "auto" },
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Select plans to delete:
                    </Typography>
                    {rowSelectionModel.length > 0 ? (
                        <Button
                            className="delBtn"
                            variant="contained"
                            sx={{ bgcolor: `error.light` }}
                            onClick={() => multiDelete(rowSelectionModel)}
                        >
                            Delete
                        </Button>
                    ) : (
                        <Button
                            disabled
                            className="delBtn"
                            variant="contained"
                            sx={{ bgcolor: `error.light` }}
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
            </Box>
            <ReviewPage status={review} change={setReview}/>
            {!customerFiles?
                <div style={{justifyItems:'center'}}>
                    <p>To begin accepting plans please connect with a processor. They will appear here after your request has been accepted.</p>
                    <div style={{width:'17%'}}>
                        <NavItem
                            href='/findProcessor'
                            key='findProcessor'
                            title='Find Processor'
                            icon={SearchIcon}
                        />
                    </div>
                </div>
                :
                <DataGrid
                    style={{textAlign:'left'}}
                    checkboxSelection
                    onRowSelectionModelChange={(newRowSelectionModel, params) => {
                        setRowSelectionModel(newRowSelectionModel);
                    }}
                    rowSelectionModel={rowSelectionModel}
                    isRowSelectable={(params) =>
                        canManagePlanInbox
                    }
                    disableRowSelectionOnClick
                    columns={columns}
                    rows={rows}
                    loading={deleteFiles.isLoading || isDeleteGridLoading}
                    pageSizeOptions={[10, 25, 50, 100]}
                    rowCount={rowCount}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                />
            }
            <AgreementModal
                open={open}
                onClose={handleClose}
                title="Downloader Agreement"
                description="Read and accept the below User Agreement to download plans."
                pdfUrl={pdfUrl}
                checked={pdfStatus}
                onToggle={changePDFStatus}
                acceptLabel="Accept Download Policy"
            />
            <Modal className="invoiceModal" open={invoiceModal} onClose={handleInvoiceClose}>
                <div>
                    <Button onClick={() => setInvoiceModal(false)}>Close</Button>
                    <EstimateView item={invoiceModelObj} handleInvoiceClose={handleInvoiceClose} selectedCompanyValues={selectedCompanyValues}/>
                </div>
            </Modal>
            <Modal
                open={validationView}
                onClose={handleCloseValidationView}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                className="estimateModal"
                style={{height:'100%', width: '100%'}}
                hideBackdrop={true}
            >
                <div className="estimateDiv" style={{height:'100%', width: '100%', }}>
                    <StaticValidationView className="estimateDiv" handleCloseValidationView={handleCloseValidationView} />
                </div>
            </Modal>
        </Box>
        </Page>
    )
};

export default PlanInboxTable;